import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { mutationNotif } from '#/utils/mutationNotif';
import {
  appendOperatorNote,
  chargeTicket,
  completeTicket,
  exportCompletedTicket,
  getTicketById,
  listTickets,
  markNoFee,
  markQueryFailed,
} from './tickets';
import type {
  AddOperatorNoteBody,
  ChargeTicketBody,
  CompleteTicketBody,
  ExportCompletedTicketParams,
  ListTicketsParams,
} from './tickets';
import {
  HighwayMaintenanceFeePenaltyTicketSchema,
  HighwayMaintenanceFeePenaltyTicketSummarySchema,
} from '../model';

export const HIGHWAY_MAINTENANCE_FEE_PENALTY_TICKET_QUERY_NAMESPACE =
  'highwayMaintenanceFeePenaltyTicket' as const;

// === Queries ===

export function useHighwayMaintenanceFeePenaltyTickets(
  params?: ListTicketsParams,
) {
  return useQuery({
    queryKey: [
      HIGHWAY_MAINTENANCE_FEE_PENALTY_TICKET_QUERY_NAMESPACE,
      'list',
      params,
    ],
    queryFn: async () => {
      const res = await listTickets({
        page: 1,
        pageSize: 999,
        ...params,
      });
      const validData = res.data.filter((item) => {
        const parsed =
          HighwayMaintenanceFeePenaltyTicketSummarySchema.safeParse(item);
        if (!parsed.success) {
          console.error(
            '[useHighwayMaintenanceFeePenaltyTickets] invalid item removed',
            item,
            parsed.error,
          );
        }
        return parsed.success;
      });
      return { ...res, data: validData };
    },
  });
}

export function useHighwayMaintenanceFeePenaltyTicket(id?: string | undefined) {
  return useQuery({
    queryKey: [
      HIGHWAY_MAINTENANCE_FEE_PENALTY_TICKET_QUERY_NAMESPACE,
      'item',
      id,
    ],
    enabled: !!id,
    queryFn: async () => {
      if (!id) return null;
      const res = await getTicketById(id);
      const parsed = HighwayMaintenanceFeePenaltyTicketSchema.safeParse(res);
      if (!parsed.success) {
        console.error(
          '[useHighwayMaintenanceFeePenaltyTicket] invalid response, returning null',
          res,
          parsed.error,
        );
        return null;
      }
      return parsed.data;
    },
  });
}

// === Mutations ===
export function useExportCompletedTicket() {
  const NOTIF_ID = 'hmfp-completed-ticket-export';
  return useMutation({
    mutationFn: (params: ExportCompletedTicketParams) =>
      exportCompletedTicket(params),
    onMutate: () => {
      mutationNotif.loading(NOTIF_ID, {
        title: '公路養管費罰鍰已繳清單',
        message: '正在匯出公路養管費罰鍰已繳清單',
      });
    },
    onSuccess: ({ blob, filename }) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      mutationNotif.toSuccess(NOTIF_ID, '已匯出公路養管費罰鍰已繳清單');
    },
    onError: () => {
      mutationNotif.toError(NOTIF_ID, '資料匯出時發生問題，請稍後再試。');
    },
  });
}

export function useMarkNoFee() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      markNoFee(id, { reason }),
    onMutate: ({ id }) => {
      mutationNotif.loading(id, {
        title: '標記無需收費',
        message: '標記中...',
      });
      return { notifId: id };
    },
    onSuccess: (_, { id }, context) => {
      queryClient.invalidateQueries({
        queryKey: [
          HIGHWAY_MAINTENANCE_FEE_PENALTY_TICKET_QUERY_NAMESPACE,
          'list',
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [
          HIGHWAY_MAINTENANCE_FEE_PENALTY_TICKET_QUERY_NAMESPACE,
          'item',
          id,
        ],
      });
      if (!context?.notifId) return;
      mutationNotif.toSuccess(context.notifId, '已標記為無需收費');
    },
    onError: (_, __, context) => {
      if (!context?.notifId) return;
      mutationNotif.toError(context.notifId, '失敗請稍後再試');
    },
  });
}

export function useMarkQueryFailed() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      markQueryFailed(id, { reason }),
    onMutate: ({ id }) => {
      mutationNotif.loading(id, {
        title: '標記查詢失敗',
        message: '標記中...',
      });
      return { notifId: id };
    },
    onSuccess: (_, { id }, context) => {
      queryClient.invalidateQueries({
        queryKey: [
          HIGHWAY_MAINTENANCE_FEE_PENALTY_TICKET_QUERY_NAMESPACE,
          'list',
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [
          HIGHWAY_MAINTENANCE_FEE_PENALTY_TICKET_QUERY_NAMESPACE,
          'item',
          id,
        ],
      });
      if (!context?.notifId) return;
      mutationNotif.toSuccess(context.notifId, '已標記查詢失敗');
    },
    onError: (_, __, context) => {
      if (!context?.notifId) return;
      mutationNotif.toError(context.notifId, '失敗請稍後再試');
    },
  });
}

export function useChargeTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & ChargeTicketBody) =>
      chargeTicket(id, body),
    onMutate: ({ id }) => {
      mutationNotif.loading(id, { title: '標記請款', message: '標記中...' });
      return { notifId: id };
    },
    onSuccess: (_, { id }, context) => {
      queryClient.invalidateQueries({
        queryKey: [
          HIGHWAY_MAINTENANCE_FEE_PENALTY_TICKET_QUERY_NAMESPACE,
          'list',
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [
          HIGHWAY_MAINTENANCE_FEE_PENALTY_TICKET_QUERY_NAMESPACE,
          'item',
          id,
        ],
      });
      if (!context?.notifId) return;
      mutationNotif.toSuccess(context.notifId, '請款成功');
    },
    onError: (_, __, context) => {
      if (!context?.notifId) return;
      mutationNotif.toError(
        context.notifId,
        '請款失敗！請設定是否需要重新排程',
      );
    },
  });
}

export function useCompleteTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & CompleteTicketBody) =>
      completeTicket(id, body),
    onMutate: ({ id }) => {
      mutationNotif.loading(id, { title: '確認代繳', message: '確認中...' });
      return { notifId: id };
    },
    onSuccess: (_, { id }, context) => {
      queryClient.invalidateQueries({
        queryKey: [
          HIGHWAY_MAINTENANCE_FEE_PENALTY_TICKET_QUERY_NAMESPACE,
          'list',
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [
          HIGHWAY_MAINTENANCE_FEE_PENALTY_TICKET_QUERY_NAMESPACE,
          'item',
          id,
        ],
      });
      if (!context?.notifId) return;
      mutationNotif.toSuccess(context.notifId, '已確認代繳完成');
    },
    onError: (_, __, context) => {
      if (!context?.notifId) return;
      mutationNotif.toError(context.notifId, '失敗請稍後再試');
    },
  });
}

export function useAddOperatorNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & AddOperatorNoteBody) =>
      appendOperatorNote(id, body),
    onMutate: ({ id }) => {
      mutationNotif.loading(id, { title: '新增備註', message: '新增中...' });
      return { notifId: id };
    },
    onSuccess: (_, { id }, context) => {
      queryClient.invalidateQueries({
        queryKey: [
          HIGHWAY_MAINTENANCE_FEE_PENALTY_TICKET_QUERY_NAMESPACE,
          'item',
          id,
        ],
      });
      if (!context?.notifId) return;
      mutationNotif.toSuccess(context.notifId, '備註已新增');
    },
    onError: (_, __, context) => {
      if (!context?.notifId) return;
      mutationNotif.toError(context.notifId, '失敗請稍後再試');
    },
  });
}
